/* eslint-disable react/prop-types */
import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  Form,
  Input,
  Button,
  Row,
  Col,
  Spin,
  Card,
  DatePicker,
  Tabs,
  message,
  Typography,
  Divider,
} from 'antd';
import {
  DeleteOutlined,
  EnvironmentOutlined,
  InfoCircleOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import Direccion from 'components/Direccion';
import Table from 'components/Table';
import {
  generateCURPRules,
  generateEmailRules,
  generateINERules,
  generateRFCRules,
  generatePhoneRules,
} from 'utils/rules';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import LadaNumero from 'components/LadaNumero';
import AvatarUploader from 'components/AvatarUploader';
import Select from 'components/Select';
import { onError } from 'utils/handlers';
import makeMessager from 'utils/complexFormsMessages';
import { makeGetOnFinish, makeOnChangeTabKey } from 'utils/tabsManagement';
import { getEstadosCiviles, getGeneros } from 'api/usuarios';
import {
  deleteCiudadano,
  getCiudadano,
  getCiudadanos,
  patchCiudadano,
  postCiudadano,
} from 'api/cuentaunicasir';
import { DATE_FRONTEND_FORMAT } from 'utils/formatValues';
import { ActionsNames, getModelPermission, hasActionPermission } from 'utils/permission/index';
import { getTiposDeConsiderados } from 'api/cuentaunicasir/tipoDeConsiderado';
import Considerados from 'components/TableConsiderados';
import { capitalize, toUpper } from 'utils/normalizers';
import NumericInput from 'components/NumericInput';
// import { postPadronConsiderado } from 'api/cuentaunicasir/padronesConsiderados';

const { TabPane } = Tabs;
const baseURI = 'cuentaunicasir/ciudadanos/';

const models = ['genero', 'estadocivil'];

const Ciudadano = ({ permission }) => {
  const addressPermission = useMemo(() => getModelPermission('direccion'), []);
  const [form] = Form.useForm();
  const [formInfo] = Form.useForm();
  const [formSearch] = Form.useForm();
  const generos = useSelector(({ catalogs }) => catalogs.generos);
  const entidad = useSelector(({ auth }) => auth.entidad);
  const estadosCiviles = useSelector(({ catalogs }) => catalogs.estadosCiviles);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [currentTabKey, setCurrentTabKey] = useState('general');
  const [formAddress, setFormAddress] = useState();
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState({ results: [] });
  const [currentPage, setCurrentPage] = useState(1);
  const [hasAllViewPermission, setHasAllViewPermission] = useState(true);
  const [tipoConsiderado, setTipoConsiderado] = useState([]);
  const [ciudadanoConsiderado, setCiudadanoConsiderado] = useState(false);
  const [justView, setJustView] = useState(false);

  const basicKeys = ['general', 'info'];
  const complexForms = [
    { key: 'direccion', formInstance: formAddress, permission: addressPermission }];

  const fetchData = async (page = 1, q = null) => {
    setLoading(true);
    setCurrentPage(page);
    const _data = await getCiudadanos({
      ...q,
      page,
    });
    setData(_data);
    setLoading(false);
  };

  const showMsg = makeMessager(setCurrentTabKey, fetchData);

  useEffect(() => {
    let mounted = true;
    let _hasAllPermission = true;
    const validatePermission = () => {
      const _hasAllViewPermission = models
        .map((m) => hasActionPermission(m, ActionsNames.view))
        .every((e) => e);
      if (!_hasAllViewPermission) {
        message.warn('Hacen falta permisos en los catálogos relacionados a este módulo');
      }
      if (mounted) {
        _hasAllPermission = _hasAllViewPermission;
        setHasAllViewPermission(_hasAllViewPermission);
      }
    };
    const fetchAll = async () => {
      setLoading(true);
      const [_tiposConsiderado] = await Promise.all([
        getTiposDeConsiderados(),
        getEstadosCiviles(),
        getGeneros(),
      ]);
      if (mounted) {
        setTipoConsiderado(_tiposConsiderado);
        setLoading(false);
      }
    };
    validatePermission();
    if (_hasAllPermission) {
      fetchAll();
    }
    return () => { mounted = false; };
  }, []);

  const onCancel = () => {
    setVisible(false);
    form.resetFields();
    formInfo.resetFields();
    setSelectedRowKeys([]);
    setCurrentTabKey('general');
    setData({ results: [] });
    setJustView(false);
  };

  const wipe = () => {
    formSearch.resetFields();
    setData({ results: [] });
  };

  const onClickEdit = async (ciudadanoId) => {
    const key = selectedRowKeys[0] || ciudadanoId;
    const match = await getCiudadano(key);
    setCiudadanoConsiderado(match.es_ciudadano_considerado);
    formInfo.setFieldsValue(match);
    form.setFieldsValue(match);
    setVisible(true);
  };

  const onFinish = async (_continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      await formInfo.validateFields();
      const values = {
        ...form.getFieldsValue(),
        ...formInfo.getFieldsValue(),
      };
      if (!values.es_ciudadano_considerado) {
        values.tipo_de_considerado = null;
      }
      const [key] = selectedRowKeys;
      const _data = await (key ? patchCiudadano(key, values) : postCiudadano(values));
      if (_data) {
        if (_continue) {
          if (!key) {
            setSelectedRowKeys([_data.id]);
          }
          await onClickEdit(_data.id);
        } else {
          onCancel();
        }
        setLoading(false);
        return _data;
      }
      setLoading(false);
      return true;
    } catch (err) {
      onError(err, setLoading, [form, formInfo], setCurrentTabKey);
      return false;
    }
  };

  const deleteItem = async () => {
    setLoading(true);
    if (selectedRowKeys.length) {
      const [key] = selectedRowKeys;
      const deleted = await deleteCiudadano(key);
      if (deleted) {
        setVisibleAlert(false);
        onCancel();
      }
    }
    setLoading(false);
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(record);
    formInfo.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAdd = () => {
    onCancel();
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const handleSearch = async () => {
    try {
      setLoading(true);
      await formSearch.validateFields();
      const params = formSearch.getFieldsValue();
      const keys = Object.keys(params);
      const validProps = keys.filter((prop) => params[prop]);
      if (validProps?.length) {
        const _data = await getCiudadanos(params);
        setData(_data);
      } else {
        message.warn('Debe ingresar al menos un campo.');
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const columns = [
    {
      titleText: 'Clave',
      dataIndex: 'clave_ciudadana',
      key: 'clave_ciudadana',
      width: 150,
    },
    {
      titleText: 'Nombre',
      dataIndex: 'nombre',
      key: 'nombre',
      width: 200,
    },
    {
      titleText: 'Apellido Paterno',
      dataIndex: 'apellido_paterno',
      key: 'apellido_paterno',
      width: 200,
    },
    {
      titleText: 'Apellido Materno',
      dataIndex: 'apellido_materno',
      key: 'apellido_materno',
      width: 200,
    },
    {
      titleText: 'Correo Electrónico',
      dataIndex: 'email',
      key: 'email',
      width: 250,
    },
    {
      titleText: 'CURP',
      dataIndex: 'CURP',
      key: 'CURP',
      width: 250,
    },
    {
      titleText: 'RFC',
      dataIndex: 'RFC',
      key: 'RFC',
      width: 250,
    },
  ];

  const rules = {
    required: [
      { required: true },
    ],
    rfc: generateRFCRules({
      validateUnique: true,
      tipoDePadron: 1,
      tipoDePersona: 1,
      id: selectedRowKeys[0],
    }),
    INE: generateINERules({ validateUnique: true, tipoDePadron: 1, id: selectedRowKeys[0] }),
    curp: generateCURPRules({ validateUnique: true, tipoDePadron: 1, id: selectedRowKeys[0] }),
    email: generateEmailRules({
      required: true,
      tipoDePadron: 1,
      id: selectedRowKeys[0],
      validateUnique: true,
    }),
    numero_de_celular: generatePhoneRules({
      tipoDePadron: 1,
      validateUnique: true,
      id: selectedRowKeys[0],
    }),
    email_alternativo: [
      { type: 'email' },
    ],
  };

  const onChangeTabKey = makeOnChangeTabKey(
    basicKeys, complexForms, currentTabKey, setCurrentTabKey, showMsg,
  );

  const getOnFinish = makeGetOnFinish(basicKeys, complexForms, currentTabKey, onFinish);

  const disableSelect = !((!!selectedRowKeys.length || entidad?.ciudadano_publico_en_general)
    && entidad?.ciudadano_publico_en_general !== selectedRowKeys?.[0]);

  return (
    <Row align="center" justify="center" className="container">
      <Spin tip="Cargando..." spinning={loading}>
        {!visible ? (
          <>
            <Form
              layout="vertical"
              form={formSearch}
              scrollToFirstError
              onFinish={() => handleSearch()}
            >
              <Row gutter={10}>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="clave_ciudadana"
                    label="Clave"
                    hasFeedback
                    normalize={capitalize}
                  >
                    <Input allowClear />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="nombre"
                    label="Nombre"
                    hasFeedback
                  >
                    <Input allowClear />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="apellido_paterno"
                    label="Apellido paterno"
                    hasFeedback
                  >
                    <Input allowClear />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="apellido_materno"
                    label="Apellido materno"
                    hasFeedback
                  >
                    <Input allowClear />
                  </Form.Item>
                </Col>
                {/* {tipoDePadron === 15 && (
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="razon_social"
                    label="Razón Social"
                    hasFeedback
                  >
                    <Input allowClear />
                  </Form.Item>
                </Col>
                )} */}
                <Col md={8} sm={24} xs={24}>
                  <Form.Item
                    label="Género"
                    name="genero"
                    normalize={capitalize}
                  >
                    <Select dataSource={generos} />
                  </Form.Item>
                </Col>
                <Col md={8} sm={24} xs={24}>
                  <Form.Item
                    label="Estado civil"
                    name="estado_civil"
                    normalize={capitalize}
                  >
                    <Select dataSource={estadosCiviles} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="email"
                    label="Correo electrónico"
                    hasFeedback
                  >
                    <Input allowClear />
                  </Form.Item>
                </Col>
                <Col md={16} sm={24} xs={24}>
                  <LadaNumero
                    form={form}
                    rules={rules}
                    fullSize
                    names={{
                      lada: 'lada',
                      numero_de_telefono: 'numero_de_celular',
                    }}
                    celular

                  />
                </Col>
                <>
                  <Col span={24}>
                    <Divider style={{ marginTop: 0 }}>Por Dirección</Divider>
                  </Col>
                  <Col md={6} sm={24} xs={24}>
                    <NumericInput
                      label="Código postal"
                      name="direccion__codigo_postal"
                    />
                  </Col>
                  <Col md={6} sm={24} xs={24}>
                    <Form.Item
                      label="Calle principal"
                      name="direccion__calle_principal"
                      normalize={capitalize}
                    >
                      <Input allowClear />
                    </Form.Item>
                  </Col>
                  <Col md={6} sm={24} xs={24}>
                    <Form.Item
                      label="Número exterior"
                      name="direccion__numero_exterior"
                      normalize={capitalize}
                    >
                      <Input allowClear />
                    </Form.Item>
                  </Col>
                </>
                <Col xs={24} sm={24} md={6}>
                  <Button
                    onClick={handleSearch}
                    block
                    style={{ marginTop: 21 }}
                  >
                    <SearchOutlined />
                    <Typography.Text>
                      Buscar
                    </Typography.Text>
                  </Button>
                </Col>
                <Col xs={24} sm={24} md={6}>
                  <Button
                    onClick={wipe}
                    block
                    style={{ marginTop: 21 }}
                  >
                    <DeleteOutlined />
                    <Typography.Text>
                      Limpiar Busqueda
                    </Typography.Text>
                  </Button>
                </Col>
              </Row>
            </Form>
            <Table
              cols={columns}
              mobileColumns={[columns[0], columns[1]]}
              data={data.results || []}
              rowSelection={rowSelection}
              handleOnRowClick={handleOnRowClick}
              controls={{
                onClickAdd,
                onClickEdit,
                onClickDelete,
                onClickExpand: () => {
                  setJustView(true);
                  onClickEdit();
                },
              }}
              rowKey="id"
              baseURI={baseURI}
              permission={permission}
              pagination={false}
              paginationCallback={fetchData}
              count={data.count}
              currentPage={currentPage}
              hasAllViewPermission={hasAllViewPermission}
              allowExpand
            />
          </>
        ) : (
          <Col span={24} style={{ height: '100%' }}>
            <Card
              className="form-container"
              title={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Ciudadano`}
              extra={(
                <FormSubmitControls
                  onFinish={justView ? null : getOnFinish()}
                  onCancel={onCancel}
                  allowSaveAndContinue={!justView}
                  onCancelText="Cancelar"
                  permission={permission}
                />
                )}
              bordered={false}
            >
              <Tabs
                onChange={onChangeTabKey}
                activeKey={currentTabKey}
                type="card"
              >
                <TabPane
                  tab={(
                    <span>
                      <InfoCircleOutlined />
                      Información General
                    </span>
                    )}
                  key="general"
                  forceRender
                >
                  <Form
                    name="general"
                    layout="vertical"
                    form={form}
                    onFinish={() => onFinish()}
                    scrollToFirstError
                  >
                    <Row gutter={10}>
                      {!!selectedRowKeys.length && (
                      <Col xs={24} sm={24} md={8}>
                        <Form.Item
                          name="clave_ciudadana"
                          label="Clave ciudadana"
                          hasFeedback
                        >
                          <Input allowClear disabled />
                        </Form.Item>
                      </Col>
                      )}
                      <Col xs={24} sm={24} md={8}>
                        <Form.Item
                          name="nombre"
                          rules={rules.required}
                          label="Nombre"
                          hasFeedback
                          normalize={capitalize}
                        >
                          <Input disabled={justView} allowClear />
                        </Form.Item>
                      </Col>

                      <Col xs={24} sm={24} md={8}>
                        <Form.Item
                          name="apellido_paterno"
                          rules={rules.required}
                          label="Apellido Paterno"
                          hasFeedback
                          normalize={capitalize}
                        >
                          <Input disabled={justView} allowClear />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={24} md={8}>
                        <Form.Item
                          name="apellido_materno"
                          rules={rules.second_last_name}
                          label="Apellido Materno"
                          hasFeedback
                          normalize={capitalize}
                        >
                          <Input disabled={justView} allowClear />
                        </Form.Item>
                      </Col>
                      <Col md={8} sm={24} xs={24}>
                        <Form.Item
                          label="Género"
                          name="genero"
                        >
                          <Select disabled={justView} dataSource={generos} />
                        </Form.Item>
                      </Col>
                      <Col md={8} sm={24} xs={24}>
                        <Form.Item
                          label="Estado civil"
                          name="estado_civil"
                        >
                          <Select disabled={justView} dataSource={estadosCiviles} />
                        </Form.Item>
                      </Col>
                      <Col lg={8} md={16} sm={24} xs={24}>
                        <LadaNumero
                          form={form}
                          rules={rules}
                          fullSize
                          names={{
                            lada: 'lada',
                            numero_de_telefono: 'numero_de_celular',
                          }}
                          celular
                          disabled={justView}
                        />
                      </Col>
                      <Col xs={24} sm={24} md={8}>
                        <Form.Item
                          name="email"
                          rules={rules.email}
                          label="Correo electrónico"
                          hasFeedback
                        >
                          <Input disabled={justView} allowClear />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={24} md={8}>
                        <Form.Item
                          name="email_alternativo"
                          rules={rules.email_alternativo}
                          label="Correo alternativo"
                          hasFeedback
                        >
                          <Input disabled={justView} allowClear />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Form.Item hidden>
                      <Button htmlType="submit" />
                    </Form.Item>
                  </Form>
                </TabPane>
                <TabPane
                  tab={(
                    <>
                      <InfoCircleOutlined />
                      Información del Ciudadano
                    </>
                    )}
                  key="info"
                  forceRender
                >
                  <Form
                    name="info"
                    layout="vertical"
                    form={formInfo}
                    onFinish={() => onFinish()}
                    scrollToFirstError
                    initialValues={{ es_ciudadano_considerado: false }}
                  >
                    <Row gutter={10}>
                      <Col xs={24} sm={24} md={8}>
                        <AvatarUploader disabled={justView} />
                      </Col>
                      <Col xs={24} sm={24} md={8}>
                        <Form.Item
                          name="RFC"
                          rules={rules.rfc}
                          label="RFC"
                          hasFeedback
                          normalize={toUpper}
                        >
                          <Input allowClear disabled={justView} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={24} md={8}>
                        <Form.Item
                          name="CURP"
                          label="CURP"
                          hasFeedback
                          normalize={toUpper}
                          rules={rules.curp}
                        >
                          <Input allowClear disabled={justView} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={24} md={8}>
                        <Form.Item
                          name="INE"
                          rules={rules.required}
                          label="INE"
                          hasFeedback
                          normalize={toUpper}
                        >
                          <Input allowClear disabled={justView} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={24} md={8}>
                        <Form.Item
                          name="fecha_nacimiento"
                          label="Fecha de nacimiento"
                          hasFeedback
                        >
                          <DatePicker
                            placeholder=""
                            format={DATE_FRONTEND_FORMAT}
                            disabled={justView}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={24} md={8}>
                        <Form.Item
                          name="es_ciudadano_considerado"
                          rules={rules.required}
                          label="Es Ciudadano Considerado"
                          hasFeedback
                        >
                          <Select
                            trueFalse
                            onChange={setCiudadanoConsiderado}
                            disabled={disableSelect || justView}
                          />
                        </Form.Item>
                      </Col>
                      {(ciudadanoConsiderado) && (
                      <>
                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            name="tipo_de_considerado"
                            label="Tipo de Considerado"
                            hasFeedback
                          >
                            <Select disabled={justView} dataSource={tipoConsiderado} />
                          </Form.Item>
                        </Col>
                        <Considerados ciudadano={selectedRowKeys[0]} />
                      </>
                      )}
                    </Row>
                    <Form.Item name="id" hidden>
                      <Input />
                    </Form.Item>
                    <Form.Item hidden>
                      <Button htmlType="submit" />
                    </Form.Item>
                  </Form>
                </TabPane>
                {addressPermission?.view && (
                <TabPane
                  tab={(
                    <>
                      <EnvironmentOutlined />
                      Dirección
                    </>
                      )}
                  key="direccion"
                  disabled={!selectedRowKeys.length}
                  forceRender={selectedRowKeys.length}
                >
                  <Direccion
                    currentURI={baseURI}
                    mutable={!justView}
                    parentID={selectedRowKeys[0]}
                    setLoading={setLoading}
                    setFormAddress={setFormAddress}
                    setCurrentTabKey={setCurrentTabKey}
                    addressPropName="direccion"
                    callback={(values, _continue) => {
                      if (!_continue) {
                        onCancel();
                      } else {
                        const [key] = selectedRowKeys;
                        const clone = [...data];
                        const mappedData = clone.map((e) => (e.id === key
                          ? ({ ...e, direccion: values?.id }) : e));
                        setData(mappedData);
                      }
                    }}
                    permission={addressPermission}
                  />
                </TabPane>
                )}
              </Tabs>
            </Card>
          </Col>
        )}
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          visible={visibleAlert}
          content={`Ciudadano con el correo: ${form.getFieldValue('email')}`}
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default Ciudadano;
